import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import {
  graphql,
  useLazyLoadQuery,
  useFragment,
  useRelayEnvironment,
} from 'react-relay';

import { useMeta, usePooling } from '@woovi/hooks';
import type { FetchKeyProp } from '@woovi/relay';
import { withRelayBoundary } from '@woovi/relay';
import { CHARGE_STATUS_ENUM, EventType } from '@woovi/shared';

import type {
  OrderApp_charge$data,
  OrderApp_charge$key,
} from './__generated__/OrderApp_charge.graphql.ts';
import type { OrderApp_query$key } from './__generated__/OrderApp_query.graphql.ts';
import type { OrderAppGetChargeQuery } from './__generated__/OrderAppGetChargeQuery.graphql.ts';
import fetchChargeQuery from './fetchChargeQuery.tsx';
import Order from './Order.tsx';
import MetaPixelScript from '../plugin/MetaPixelScript.tsx';
import { notifyListeners } from '../plugin/notifyListeners.tsx';
import { PLUGIN_POLLING_INTERVAL } from '../poolingInterval.tsx';
import useStoreContext from '../store/useStoreContext.tsx';
import { useChargeUpdateStatusSubscription } from '../subscription/useChargeUpdatedStatus.ts';

export type OrderAppProps = {
  appID: string;
  correlationID: string;
  paymentLinkID: string;
  isTemp: boolean;
  isCharging: boolean;
  companyName: string;
} & FetchKeyProp;

const canRedirectUser = (
  charge?: OrderApp_charge$data | null,
  initialStatus?: string | null,
): boolean => {
  const hasRedirectUrl = !!charge?.redirectUrl;
  const isCompletedStatus = charge?.status === CHARGE_STATUS_ENUM.COMPLETED;
  const isChargeInitiallyCompleted =
    initialStatus === CHARGE_STATUS_ENUM.COMPLETED;

  return hasRedirectUrl && isCompletedStatus && !isChargeInitiallyCompleted;
};

const useNotify = (charge: OrderApp_charge$data): void => {
  const initialStatus = useRef(charge?.status);

  useEffect(() => {
    if (charge?.status === CHARGE_STATUS_ENUM.EXPIRED) {
      notifyListeners({
        type: EventType.CHARGE_EXPIRED,
        data: {},
      });
    }

    if (charge?.status === CHARGE_STATUS_ENUM.COMPLETED) {
      notifyListeners({
        type: EventType.CHARGE_COMPLETED,
        data: {},
      });

      const data = {
        status: CHARGE_STATUS_ENUM.COMPLETED,
      };

      notifyListeners({
        type: EventType.PAYMENT_STATUS,
        data,
      });
    }

    if (canRedirectUser(charge, initialStatus.current)) {
      location.href = charge.redirectUrl;
    }
  }, [charge?.status]);
};

const OrderApp = ({
  correlationID,
  paymentLinkID,
  isCharging = true,
  fetchKey,
}: OrderAppProps): ReactNode => {
  const { setOpenPixState } = useStoreContext();
  const environment = useRelayEnvironment();

  const response = useLazyLoadQuery<OrderAppGetChargeQuery>(
    graphql`
      query OrderAppGetChargeQuery(
        $correlationID: String
        $paymentLinkID: String
      ) {
        company {
          hasFeatureTemp
          projectType
        }
        charge(correlationID: $correlationID) {
          ...OrderApp_charge
        }
        ...OrderApp_query
        paymentLink(paymentLinkID: $paymentLinkID) {
          ...OrderApp_paymentLink
          charge {
            ...OrderApp_charge
            ...OrderApp_chargePaymentLink
          }
          pixQrCode {
            id
          }
        }
      }
    `,
    {
      correlationID,
      paymentLinkID,
    },
    {
      fetchPolicy: 'store-or-network',
      fetchKey,
    },
  );

  const query = useFragment<OrderApp_query$key>(
    graphql`
      fragment OrderApp_query on Query {
        ...Order_query
      }
    `,
    response,
  );

  const chargeData = useFragment<OrderApp_charge$key>(
    graphql`
      fragment OrderApp_charge on Charge {
        ...Order_charge
        ...useMeta_charge
        id
        status
        redirectUrl
        correlationID
        metaPixel {
          token
        }
      }
    `,
    response.charge,
  );

  const chargePaymentLink = useFragment<OrderApp_charge$key>(
    graphql`
      fragment OrderApp_chargePaymentLink on Charge {
        ...Order_charge
        ...useMeta_charge
        id
        status
        redirectUrl
        correlationID
        metaPixel {
          token
        }
      }
    `,
    response.paymentLink?.charge,
  );

  const paymentLink = useFragment<OrderApp_charge$key>(
    graphql`
      fragment OrderApp_paymentLink on PaymentLink {
        ...Order_paymentLink
      }
    `,
    response.paymentLink,
  );

  // move this somewhere else
  const charge = chargeData || chargePaymentLink;

  useChargeUpdateStatusSubscription({
    input: {
      chargeId: charge?.id,
    },
  });

  const isChargeCompleted = charge?.status === CHARGE_STATUS_ENUM.COMPLETED;
  const isChargeExpired = charge?.status === CHARGE_STATUS_ENUM.EXPIRED;
  // improve should pooling to cobv
  const shouldPooling = !!charge && !isChargeCompleted && !isChargeExpired;

  usePooling(
    shouldPooling,
    () => fetchChargeQuery(environment, correlationID, setOpenPixState),
    PLUGIN_POLLING_INTERVAL,
  );

  useMeta({ charge });

  const getMetaPixelScript = (): ReactNode => {
    const token = charge?.metaPixel?.token;

    if (!token) {
      return null;
    }

    return <MetaPixelScript token={token} charge={charge} />;
  };

  useNotify(charge);

  return (
    <>
      <Order
        query={query}
        charge={charge}
        isCharging={isCharging}
        correlationID={correlationID || charge?.correlationID}
        paymentLink={paymentLink}
      />
      {getMetaPixelScript()}
    </>
  );
};

export default withRelayBoundary(OrderApp);
